import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { selectDarkMode } from './store/userPreferencesSlice';
import { theme, darkTheme } from './theme';
import './styles/theme.css';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import SplashScreen from './components/SplashScreen';

// Pages
import Dashboard from './pages/Dashboard';
import TranscriptionPage from './pages/TranscriptionPage';
import TranscriptionList from './pages/TranscriptionList';
import TranscriptionHistoryPage from './pages/TranscriptionHistoryPage';
import ReportEditorPage from './pages/ReportEditorPage';
import SessionDetail from './pages/SessionDetail';
import LoginPage from './pages/LoginPage';
import UserManagementPage from './pages/UserManagementPage';
import AdminConfigPage from './pages/AdminConfigPage';
import AdminMonitorPage from './pages/AdminMonitorPage';
import PatientSearchPage from './pages/PatientSearchPage';
import PromptManagementPage from './pages/admin/PromptManagementPage';
import UserPreferencesPage from './pages/UserPreferencesPage';
import ReportingProfilesPage from './pages/ReportingProfilesPage';
import TemplatesPage from './pages/TemplatesPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';

const AppContent: React.FC = () => {
  const darkMode = useSelector(selectDarkMode);
  const [showSplash, setShowSplash] = useState(true);
  const handleSplashFinished = useCallback(() => setShowSplash(false), []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  return (
    <ThemeProvider theme={darkMode ? darkTheme : theme}>
      <CssBaseline />
      {showSplash && <SplashScreen onFinished={handleSplashFinished} />}
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="transcribe" element={<TranscriptionPage />} />
            <Route path="report/edit" element={<ReportEditorPage />} />
            <Route path="transcriptions" element={<TranscriptionHistoryPage />} />
            <Route path="patients" element={<PatientSearchPage />} />
            <Route path="patients/:patientId" element={<PatientSearchPage />} />
            <Route path="transcriptions/legacy" element={<TranscriptionList />} />
            <Route path="sessions/:sessionId" element={<SessionDetail />} />
            <Route path="admin/config" element={<AdminConfigPage />} />
            <Route path="admin/monitor" element={<AdminMonitorPage />} />
            <Route path="admin/users" element={<UserManagementPage />} />
            <Route path="admin/prompts" element={<PromptManagementPage />} />
            <Route path="user/preferences" element={<UserPreferencesPage />} />
            <Route path="user/reporting-profiles" element={<ReportingProfilesPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="admin/settings" element={<Navigate to="/admin/config" replace />} />
            <Route path="admin/system-config" element={<Navigate to="/admin/config" replace />} />
            <Route path="admin/ai-services" element={<Navigate to="/admin/config" replace />} />
            <Route path="admin/logs" element={<Navigate to="/admin/monitor" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  // Expose store for E2E test introspection (Playwright page.evaluate)
  if (typeof window !== 'undefined') {
    (window as unknown as { store: typeof store }).store = store;
  }

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppContent />
      </PersistGate>
    </Provider>
  );
};

export default App;